import { motion } from 'framer-motion';
import { SectionShell } from './SectionShell';

/**
 * Business model — hybrid pricing with modular expansion and contracted ARR.
 */

interface BusinessComponent {
  num: string;
  title: string;
  body: string;
}

const COMPONENTS: BusinessComponent[] = [
  {
    num: '01',
    title: 'Hybrid model',
    body: 'Core platform priced per seat plus a firm level platform licence for governance, audit and configuration',
  },
  {
    num: '02',
    title: 'Enterprise discounting',
    body: 'Sliding per seat rates based on volume: small firms highest £/seat, large firms lower £/seat',
  },
  {
    num: '03',
    title: 'Modular upsell',
    body: 'The expansion engine: firms add transaction processing, deeper AI agents, asset intelligence and jurisdiction packs without replacing the core platform',
  },
  {
    num: '04',
    title: 'Targeting 3 year lock in contracts',
    body: 'Targeting three year contracts that give firms commercial certainty and build durable, multi year ARR',
  },
];

export function BusinessModel() {
  return (
    <SectionShell
      id="business"
      index={7}
      density="tight"
      eyebrow="Business model"
      title="Hybrid pricing"
      intro="Per seat + platform licence is the spine. Modular upsell grows account value, while three year contracts build durable multi year ARR"
    >
      <div className="pitch-strip grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle md:grid-cols-2 lg:grid-cols-3">
        {COMPONENTS.map((c, i) => (
          <ComponentCell key={c.num} comp={c} index={i} />
        ))}
        {/* Narrative anchor */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
          className="pitch-cell relative isolate flex flex-col justify-between gap-3 overflow-hidden p-7 sm:p-8 md:col-span-2 lg:col-span-2"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 -z-10 h-48 w-48 rounded-full opacity-[0.12] blur-2xl"
            style={{ background: '#3B82F6' }}
          />
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-brand-bright">
            Built for regulated systems
          </span>
          <p className="text-balance text-[16px] leading-relaxed text-ink-strong">
            Governance, auditability and human approval are part of the core
            licence and never optional add ons
          </p>
        </motion.div>
      </div>
    </SectionShell>
  );
}

function ComponentCell({
  comp,
  index,
}: {
  comp: (typeof COMPONENTS)[number];
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.05 + index * 0.06,
      }}
      className="pitch-cell pitch-cell-interactive flex flex-col gap-3 p-7 transition-colors sm:p-8"
    >
      <span className="text-[11px] font-medium uppercase tabular-nums tracking-[0.22em] text-brand-bright">
        {comp.num}
      </span>
      <h3 className="text-balance text-[18px] font-semibold leading-snug text-ink-strong">
        {comp.title}
      </h3>
      <p className="text-[14px] leading-relaxed text-ink-muted">{comp.body}</p>
    </motion.div>
  );
}
